// Realistic preview modal. The Designer canvas is always the clean
// edit workspace; pressing Preview ships the user's design (transparent
// PNG, no mockup) + the active garment/location to /api/preview/generate.
// Server-side loads the location's AI reference image and conditions the
// Fal call on (reference + design + prompt).
//
// The modal shows ONLY the generated result — the customer doesn't need to
// see their own transparent design rendered separately. While the call is
// in flight we show an animated progress bar at the top of the result card
// so it's obvious work is happening even when the centre is still empty.
import { useCallback, useEffect, useRef, useState } from 'react'
import { fabric } from 'fabric'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../lib/api'

interface Props {
  open: boolean
  onClose: () => void
  canvas: fabric.Canvas | null
  productId: string
  locationId: string | null
  garmentName?: string
  locationName?: string
  // Legacy props from the compositing era — kept optional so the Designer's
  // existing call site doesn't break. The server ignores anything that
  // would have driven the composite step.
  aiReferenceUrl?: string | null
  aiReferenceBox?: unknown
}

interface GenResp { url: string; modelUsed: string }

export default function PreviewModal({ open, onClose, canvas, productId, locationId, garmentName, locationName }: Props) {
  const { getAccessToken } = useAuth()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const startedRef = useRef(false)

  // Export just the user's design (text + images), with a transparent
  // background. We hide the dashed print-area guide first so the AI doesn't
  // bake the outline into its output.
  const exportDesign = useCallback((): string | null => {
    if (!canvas) return null
    const objs = canvas.getObjects().slice()
    const wasVisible = new Map<fabric.Object, boolean>()
    objs.forEach((o) => {
      const isGuide = (o.type === 'rect' && Array.isArray((o as fabric.Rect).strokeDashArray))
                   || (o.type === 'text' && (o as fabric.Text).text === 'PRINT AREA')
      if (isGuide) { wasVisible.set(o, o.visible !== false); o.visible = false }
    })
    canvas.discardActiveObject()
    canvas.requestRenderAll()
    // Crop the export to the bounding box of the visible design objects.
    // Without this we ship a 640x720 PNG mostly full of transparency, which
    // wastes bytes on the request and gives the generation model a much
    // smaller usable design region to read from. The bbox is `acoords`-
    // aware so it includes rotations.
    const visible = objs.filter((o) => o.visible !== false && !wasVisible.has(o))
    let png: string
    if (visible.length === 0) {
      png = canvas.toDataURL({ format: 'png', multiplier: 1 })
    } else {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      visible.forEach((o) => {
        const r = o.getBoundingRect(true, true) // absolute, with transforms
        minX = Math.min(minX, r.left)
        minY = Math.min(minY, r.top)
        maxX = Math.max(maxX, r.left + r.width)
        maxY = Math.max(maxY, r.top + r.height)
      })
      // Small padding so the model has a sliver of transparency around the
      // design (helps it understand where the design ends, vs flush-to-edge
      // which sometimes gets cropped by the model's own padding pass).
      const PAD = 4
      const cw = canvas.getWidth()
      const ch = canvas.getHeight()
      const left   = Math.max(0,  Math.floor(minX) - PAD)
      const top    = Math.max(0,  Math.floor(minY) - PAD)
      const right  = Math.min(cw, Math.ceil(maxX)  + PAD)
      const bottom = Math.min(ch, Math.ceil(maxY)  + PAD)
      const width  = Math.max(1, right - left)
      const height = Math.max(1, bottom - top)
      png = canvas.toDataURL({ format: 'png', multiplier: 1, left, top, width, height } as unknown as fabric.IDataURLOptions)
    }
    wasVisible.forEach((v, o) => { o.visible = v })
    canvas.requestRenderAll()
    return png
  }, [canvas])

  const generate = useCallback(async () => {
    if (!canvas) return
    setBusy(true); setErr(null); setResultUrl(null)
    try {
      const design = exportDesign()
      if (!design) throw new Error('Could not capture design')
      // The server now generates fresh from a text scene description + a
      // position hint derived from the location name (no client-side
      // compositing). We just ship the flattened design and let the
      // backend describe the rest.
      const r = await apiFetch<GenResp>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        '/api/preview/generate',
        { method: 'POST', body: JSON.stringify({ designDataUrl: design, productId, locationId }) },
      )
      setResultUrl(r.url)
    } catch (e) { setErr((e as Error).message) }
    finally { setBusy(false) }
  }, [exportDesign, canvas, productId, locationId, getAccessToken])

  useEffect(() => {
    if (!open) { startedRef.current = false; setResultUrl(null); setErr(null); return }
    if (startedRef.current) return
    startedRef.current = true
    void generate()
  }, [open, generate])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow !mb-0">AI preview</div>
            <h3 className="font-heading uppercase font-bold text-xl text-ink">Your design, as if worn</h3>
            <p className="text-xs text-ink-3 mt-1">{garmentName ?? 'Garment'}{locationName ? ` · ${locationName}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-2xl leading-none shrink-0">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {err && <div className="card text-red-700 mb-4">{err}</div>}

          <div className="relative aspect-[4/3] bg-surface border border-gold rounded overflow-hidden flex items-center justify-center">
            {/* Indeterminate progress bar across the top while a call is in flight. */}
            {busy && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gold/20 overflow-hidden">
                <div className="h-full w-1/3 bg-gold animate-[slide_1.4s_ease-in-out_infinite]" style={{ animationName: 'evrstSlide' }} />
              </div>
            )}
            {busy && (
              <div className="text-center text-ink-3 px-6">
                <div className="w-10 h-10 mx-auto border-4 border-gold border-t-transparent rounded-full animate-spin" />
                <div className="text-xs mt-3">Generating realistic preview…</div>
                <div className="text-[11px] mt-1 text-ink-3">Usually 10–30 seconds.</div>
              </div>
            )}
            {!busy && resultUrl && <img src={resultUrl} alt="AI realistic preview" className="max-w-full max-h-full object-contain" />}
            {!busy && !resultUrl && !err && <div className="text-xs text-ink-3">No result yet.</div>}
          </div>

          {/* Local keyframes for the indeterminate slide bar so we don't rely on Tailwind config. */}
          <style>{`@keyframes evrstSlide { 0% { transform: translateX(-100%); } 100% { transform: translateX(400%); } }`}</style>

          <p className="text-[11px] text-ink-3 mt-3 text-center">This is AI generated and may not always be accurate.</p>
        </div>

        <div className="px-6 py-3 border-t border-border flex items-center justify-between bg-surface">
          <div className="flex items-center gap-3">
            {resultUrl && <a href={resultUrl} download="evrst-preview.png" target="_blank" rel="noreferrer" className="text-[11px] text-gold-dark hover:underline">Download ↓</a>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-outline text-xs">Close</button>
            <button onClick={generate} disabled={busy || !canvas} className="btn-gold text-xs px-4 py-2">{busy ? 'Generating…' : 'Regenerate'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
